import dynamic from 'next/dynamic';
import React from 'react';
import styled from 'styled-components';
import { useAuthentication } from '@hotelplan/components.common.auth';
import {
  ImageMediaItem,
  PdpOverviewComponent,
  AuthChannelType,
} from '@hotelplan/graphql.types';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useFeatureToggle } from '@hotelplan/libs.feature-toggle';
import { getTargetPageData } from '@hotelplan/libs.router-link-utils';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import PageLead from 'components/core/page-lead/PageLead';
import GeoMediaGallery from 'components/domain/geo/GeoMediaGallery';
import { FeatureList } from 'config/pageConfig.constants';

export interface IPdpPageLeadProps
  extends Pick<
    PdpOverviewComponent,
    | 'productName'
    | 'media'
    | 'hpRating'
    | 'productBreadcrumbs'
    | 'taRating'
    | 'featuresSummary'
    | 'disrupters'
  > {
  leadText?: string;
  children?: React.ReactElement | React.ReactElement[];
}

const PdpCheapestOffer = dynamic(() => import('./PdpCheapestOffer'), {
  ssr: false,
});

const PageLeadPdp = styled(PageLead)(
  sx2CssThemeFn({
    flexDirection: ['column-reverse', 'row-reverse'],

    '.sitetool-wrap': {
      borderRight: '1px solid #ccc',
      borderRadius: '0 0 5px 5px',
    },
  })
);

const PdpPageLead: React.FC<IPdpPageLeadProps> = ({
  productName,
  media,
  hpRating,
  productBreadcrumbs,
  taRating,
  leadText,
  featuresSummary,
  disrupters,
  children,
}: IPdpPageLeadProps): React.ReactElement => {
  const { channelType } = useAuthentication();
  const { objectId } = useObjectId();

  const isB2BAuthenticated = channelType === AuthChannelType.B2B;

  const { mobile } = useDeviceType();

  const breadCrumbItems =
    productBreadcrumbs.breadcrumbs.map(item => ({
      label: item.caption,
      link: getTargetPageData(item.link),
    })) || [];

  const mediaItems =
    (media?.mediaItems.filter(
      item => item.__typename == 'ImageMediaItem'
    ) as ImageMediaItem[]) || [];

  const { getVariant } = useFeatureToggle();

  const isImageGalleryPositionEnabled =
    getVariant(FeatureList.PDP_IMAGE_GALLERY_POSITION)?.name ===
    'reversed-gallery-position';

  const PageLeadVariant = isImageGalleryPositionEnabled
    ? PageLeadPdp
    : PageLead;

  return (
    <PageLeadVariant
      title={productName}
      text={mobile ? leadText : ''}
      rating={hpRating}
      breadcrumbs={breadCrumbItems}
      tripadvisor={{ rating: taRating?.rating, reviews: taRating?.count }}
      mediaGallery={<GeoMediaGallery items={mediaItems} />}
      offer={!mobile && <PdpCheapestOffer />}
      featuresSummary={!mobile && featuresSummary}
      wishlistIcon
      addBorderBottom
      isHeroSiteToolsEnabled
      giataId={isB2BAuthenticated ? objectId : null}
      disrupterObject={disrupters?.[0]}
    >
      {children}
    </PageLeadVariant>
  );
};

export default PdpPageLead;
